import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UserInfo } from "@models/user";
import { LocalStorageService } from "@services/local-storage.service";
import { EventCategory } from "@services/types/events-management-response.type";
import { hasInputChanged } from "@shared/helpers/hasChanges/hasChanges.helper";
import { ButtonComponent } from "../../../../../../shared/components/button/button.component";
import { BasicInfoGroup } from "../../event-form.form";

export type MediaSelection = {
  control: "uploadBanner" | "mapaEvento";
  file: File;
  blobPath: string;
};

@Component({
  selector: "app-event-basic-info-step",
  imports: [ReactiveFormsModule, ButtonComponent],
  templateUrl: "./event-basic-info-step.component.html",
  styleUrl: "./event-basic-info-step.component.scss",
})
export class EventBasicInfoStepComponent implements OnChanges {
  @Input({ required: true }) formGroup!: FormGroup<BasicInfoGroup>;
  @Input() eventCategories: EventCategory[] = [];
  @Input() initialBannerPreview: string | null = null;
  @Input() initialMapPreview: string | null = null;

  @Output() mediaSelected = new EventEmitter<MediaSelection>();
  @Output() mediaCleared = new EventEmitter<"uploadBanner" | "mapaEvento">();

  @ViewChild("bannerInput") private bannerInput?: ElementRef<HTMLInputElement>;
  @ViewChild("mapInput") private mapInput?: ElementRef<HTMLInputElement>;

  private readonly localStorageService = inject(LocalStorageService);

  uploadedBannerPreview: string | ArrayBuffer | null = null;
  uploadedMapPreview: string | ArrayBuffer | null = null;

  /** controla se o input aparece (igual referência: mostra "Alterar imagem" quando já existe preview) */
  showBannerInput = true;
  showMapInput = true;

  currentUser: UserInfo = JSON.parse(
    this.localStorageService.getItem("current_user"),
  );

  get mediaGroup() {
    return this.formGroup.controls.media;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.verifyBannerChanges(changes);
    this.verifyMapChanges(changes);
  }

  // ===== UI actions (igual referência) =====

  enableBannerChange(): void {
    this.showBannerInput = true;
    this.resetNativeFileInput("uploadBanner");
  }

  enableMapChange(): void {
    this.showMapInput = true;
    this.resetNativeFileInput("mapaEvento");
  }

  onBannerFileChange(event: Event): void {
    this.handleFileChange(event, "uploadBanner", (r) => {
      this.uploadedBannerPreview = r;
      this.showBannerInput = false; // esconde input e mostra botão "Alterar"
    });
  }

  onMapFileChange(event: Event): void {
    this.handleFileChange(event, "mapaEvento", (r) => {
      this.uploadedMapPreview = r;
      this.showMapInput = false;
    });
  }

  clearBanner(): void {
    this.clearMedia("uploadBanner");
    this.uploadedBannerPreview = null;
    this.showBannerInput = true;

    // voltou a não ter banner => volta a exigir
    this.restoreBannerRequiredValidator();

    this.resetNativeFileInput("uploadBanner");
    this.mediaCleared.emit("uploadBanner");
  }

  clearMap(): void {
    this.clearMedia("mapaEvento");
    this.uploadedMapPreview = null;
    this.showMapInput = true;

    this.resetNativeFileInput("mapaEvento");
    this.mediaCleared.emit("mapaEvento");
  }

  // ===== initial preview handling =====

  private verifyBannerChanges(changes: SimpleChanges): void {
    if (!this.initialBannerPreview) return;

    const hasChanges = hasInputChanged<
      EventBasicInfoStepComponent,
      "initialBannerPreview"
    >(changes, "initialBannerPreview");

    if (hasChanges) {
      this.uploadedBannerPreview = this.initialBannerPreview;

      // edição: já tem imagem => esconde input e não exige arquivo novo
      this.showBannerInput = false;
      this.clearBannerRequiredValidator();
    }
  }

  private verifyMapChanges(changes: SimpleChanges): void {
    if (!this.initialMapPreview) return;

    const hasChanges = hasInputChanged<
      EventBasicInfoStepComponent,
      "initialMapPreview"
    >(changes, "initialMapPreview");

    if (hasChanges) {
      this.uploadedMapPreview = this.initialMapPreview;
      this.showMapInput = false; // opcional, mas mantém padrão da referência
    }
  }

  // ===== core file handling =====

  private handleFileChange(
    ev: Event,
    control: "uploadBanner" | "mapaEvento",
    onLoad: (result: string | ArrayBuffer | null) => void,
  ): void {
    const input = ev.target as HTMLInputElement | null;
    const file = input?.files?.item(0);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => onLoad(reader.result);
    reader.readAsDataURL(file);

    this.mediaGroup.patchValue({ [control]: file });
    this.mediaGroup.controls[control].markAsDirty();
    this.mediaGroup.controls[control].updateValueAndValidity();

    // se selecionou um arquivo, não precisa mais do required "por falta de banner"
    if (control === "uploadBanner") {
      this.clearBannerRequiredValidator();
    }

    const blobPath = this.buildBlobPath(control, file);
    this.mediaSelected.emit({ control, file, blobPath });
  }

  private buildBlobPath(
    control: "uploadBanner" | "mapaEvento",
    file: File,
  ): string {
    const prefix = control === "uploadBanner" ? "banner" : "map";
    return `${prefix}-${this.currentUser.id}/${file.name}`;
  }

  private clearMedia(control: "uploadBanner" | "mapaEvento"): void {
    this.mediaGroup.patchValue({ [control]: null });
    this.mediaGroup.controls[control].markAsDirty();
    this.mediaGroup.controls[control].updateValueAndValidity();
  }

  // ===== validators (banner required only when no initial + no file) =====

  private clearBannerRequiredValidator(): void {
    const ctrl = this.mediaGroup.controls.uploadBanner;

    // só limpa se existir o validator required hoje (evita mexer em outros validators)
    ctrl.clearValidators();
    ctrl.updateValueAndValidity();
  }

  private restoreBannerRequiredValidator(): void {
    const ctrl = this.mediaGroup.controls.uploadBanner;

    // se seu form original tinha REQUIRED, restaura aqui
    ctrl.setValidators([Validators.required]);
    ctrl.updateValueAndValidity();
  }

  private resetNativeFileInput(control: "uploadBanner" | "mapaEvento"): void {
    const el =
      control === "uploadBanner"
        ? this.bannerInput?.nativeElement
        : this.mapInput?.nativeElement;

    if (el) el.value = "";
  }
}
